import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppDispatch, RootState } from '../Store';
import { Api } from '../../Api/Api';
import { ILoginRequest } from '../../Models/Requests/LoginRequest';
import { IAuthResponse, IUserAuthResponse } from '../../Models/Responses/UserAuthResponse';
import { IManageUserResponse } from '../../Models/Responses/ManageUsersResponse';
import { userInfoSlice } from './UserInfoSlice';
import { UserPermission } from '../../Models/Responses/UserPermissions';
import { requestPasswordReset } from './UserThunks';
import { NetError } from '../../Api/NetError';
import { uiSlice } from '../UI/UISlice';
import { createTracedAsyncThunk } from '../../../tracing/trace';
import { globalSession } from '../../../tracing/session';
import { EventNavigationNames } from '../../../tracing/EventNames';
import { EventAuthTags } from '../../../tracing/EventTagNames';
import { jwtDecode } from 'jwt-decode';

const api6 = new Api('/api/6', undefined, undefined);
// api7 is used for unauthenticated Okta OAuth endpoints only
const api7 = new Api('/api/7', undefined, undefined);

/*
	localStorage key tracking the most recent successful login method.
 	Used by the Login page to auto-initiate the Okta flow for users who
	previously authenticated via Okta.
 */
export const LAST_LOGIN_TYPE_KEY = 'LastLoginType';
export type LastLoginType = 'okta' | 'password';

export const loginUser = createTracedAsyncThunk<IUserAuthResponse, ILoginRequest, { dispatch: AppDispatch, state: RootState }>(
	'auth/login',
	async (context, request: ILoginRequest, thunkAPI) => 
	{
		try
		{
			thunkAPI.dispatch(uiSlice.actions.resetPostLoginProgress({ loginType: 'password' }));

			const response = await api6.postUnauthorizedAsync<IUserAuthResponse>('authentication', request);
			if (response.ErrorCode === null && response.Success)
			{
				thunkAPI.dispatch(authSlice.actions.setAuthToken(response.Data));
				thunkAPI.dispatch(userInfoSlice.actions.setUserInfo(response.Data));
				
				globalSession.initializeSession(
					response.Data.AccessToken,
					response.Data.UserId,
					response.Data.FoundationId,
					response.Data.UserEmail,
					response.Data.UserName);

				globalSession.event(EventNavigationNames.LogIn, { [EventAuthTags.LoginMethod]: 'password' });

				localStorage.setItem(LAST_LOGIN_TYPE_KEY, 'password');

				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export const tokenExchange = createTracedAsyncThunk<IAuthResponse, string, { dispatch: AppDispatch, state: RootState }>(
	'auth/exchange',
	async (context, token: string, thunkAPI) => 
	{
		try
		{
			thunkAPI.dispatch(uiSlice.actions.resetPostLoginProgress({ loginType: 'exchange_token' }));

			const response = await api6.getUnauthorizedAsync<IAuthResponse>(`sso/${ encodeURIComponent(token) }`);

			if (response.ErrorCode === null && response.Success)
			{
				thunkAPI.dispatch(authSlice.actions.setAuthToken(response.Data.User));
				thunkAPI.dispatch(userInfoSlice.actions.setUserInfo(response.Data.User));

				globalSession.initializeSession(
					response.Data.User.AccessToken,
					response.Data.User.UserId,
					response.Data.User.FoundationId,
					response.Data.User.UserEmail,
					response.Data.User.UserName);

				globalSession.event(EventNavigationNames.LogIn, { [EventAuthTags.LoginMethod]: 'exchange_token' });

				return response.Data;
			}
			else
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

interface ISsoExchangeResponse
{
	AccessToken: string;
	Fields: Omit<IUserAuthResponse, 'AccessToken'>;
	State?: Record<string, string>;
}

/**
 * Use our new v5 endpoint to trade an exchange token for an auth state.
 */
export const ssoExchange = createTracedAsyncThunk<IAuthResponse, { token:string }, { dispatch: AppDispatch, state: RootState }>(
	'sso/exchange',
	async (context, {token}, thunkAPI) => 
	{
		try
		{
			thunkAPI.dispatch(uiSlice.actions.resetPostLoginProgress({ loginType: 'sso'}));

			const response = await api6.getUnauthorizedAsync<ISsoExchangeResponse>(`sso/${ encodeURIComponent(token) }?system=fields`);

			if (response.ErrorCode === null && response.Success)
			{
				if(!response.Data.Fields)
				{
					return thunkAPI.rejectWithValue({code: 'NOT_A_USER', message: 'This account does not exist yet in GHX Fields.  Please contact your support specialist to get access.'});
				}

				const authResponse: IAuthResponse = {
					User: {
						...response.Data.Fields,
						AccessToken: response.Data.AccessToken,
					},
				};

				thunkAPI.dispatch(userInfoSlice.actions.setUserInfo({
					...authResponse.User,
					ssoLogin: true,
				}));

				globalSession.initializeSession(
					authResponse.User.AccessToken,
					authResponse.User.UserId,
					authResponse.User.FoundationId,
					authResponse.User.UserEmail,
					authResponse.User.UserName);

				globalSession.event(EventNavigationNames.LogIn, { [EventAuthTags.LoginMethod]: 'sso' });

				const intialUserFoundationId = response.Data.State?.seller;
				const initialGrowerFoundationId = response.Data.State?.grower;
				const initialOrderId = response.Data.State?.order;

				thunkAPI.dispatch(uiSlice.actions.setInitialUserFoundationId(intialUserFoundationId));
				thunkAPI.dispatch(uiSlice.actions.setInitialGrowerFoundationId(initialGrowerFoundationId));
				thunkAPI.dispatch(uiSlice.actions.setInitialSalesforceOrderId(initialOrderId));

				return authResponse;
			}
			else 
			{
				return thunkAPI.rejectWithValue({code: 'API_ERROR', message: response.ErrorMessage});
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			if(e instanceof NetError)
			{
				if(e.code === 404)
				{
					return thunkAPI.rejectWithValue({code: 'INVALID_TOKEN', message: 'The authentication token used is not valid; please log in again. If the problem persists, contact your support specialist.'});
				}
			}
			return thunkAPI.rejectWithValue({code: 'GENERAL_ERROR', message: e.message});
		}
	}
);

export const getManageableUsers = createTracedAsyncThunk<IManageUserResponse[], null, { dispatch: AppDispatch, state: RootState }>(
	'auth/manageableUsers/get',
	async (context, request: {}, thunkAPI) =>
	{
		try
		{
			const api = new Api('/api/6', thunkAPI.getState().auth.userAuthToken, context);
			const response = await api.getAsync<IManageUserResponse[]>('authorization/manageable', request);

			if (response.ErrorCode === null && response.Success)
			{
				const state = thunkAPI.getState().auth;
				const payload = response.Data;

				// If the currently managed user is not in this list, pick a different one!
				if(payload.length > 0)
				{
					if(!state.selectedUser || !payload.find(mu => mu.UserId === state.selectedUser.UserId) )
					{
						const list = payload.sort((a,b) => (a.UserName ?? a.UserEmail).localeCompare((b.UserName ?? b.UserEmail), undefined, {'sensitivity': 'base'}));
						const firstUserFromList = list[0];

						if (firstUserFromList) 
						{
							thunkAPI.dispatch(setManagedUser(firstUserFromList));
							thunkAPI.dispatch(getManageableUsers());
						}
					}
				}

				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);


/** 
 *  Get a list of all users for the troubleshooting tools.
 * !!!This is not a list of manageable users for the dashboard.!!!
 **/
export const getTroubleshooterUsers = createTracedAsyncThunk<IManageUserResponse[], null, { dispatch: AppDispatch, state: RootState }>(
	'auth/getTroubleshooterUsers/get',
	async (context, request: {}, thunkAPI) =>
	{
		try
		{
			const api = new Api('/api/6', thunkAPI.getState().auth.userAuthToken, context);
			const response = await api.getAsync<IManageUserResponse[]>('authorization/all', request);

			if (response.ErrorCode === null && response.Success)
			{

				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export const silentValidateToken = createTracedAsyncThunk<boolean, string, { state: RootState }>(
	'auth/silentValidateToken',
	async (context, token: string, thunkAPI) => 
	{
		try 
		{
			const api6 = new Api('/api/6', token, context);
			const response = await api6.postAsync<boolean>('authentication/validate', token);

			if (response.ErrorCode === null && response.Success) 
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue({type: 'api_error', message: response.ErrorMessage});
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e) 
		{
			return thunkAPI.rejectWithValue({type: 'network_error', message: e.message});
		}
	}
);

export const validateToken = createTracedAsyncThunk<boolean, string, { state: RootState }>(
	'auth/validateToken',
	async (context, token: string, thunkAPI) => 
	{
		try 
		{
			const api6 = new Api('/api/6', token, context);
			const response = await api6.postAsync<boolean>('authentication/validate', token);

			if (response.ErrorCode === null && response.Success) 
			{
				return response.Data;
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e) 
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);


/**
 * Initiates the Okta OAuth flow by requesting an authorization URL from the backend.
 * Automatically redirects the browser to Okta's login page.
 * The user will be redirected back to /okta/callback after authentication.
 */
export const initiateOktaFlow = createTracedAsyncThunk<string, { email: string }, { dispatch: AppDispatch, state: RootState }>(
	'auth/initiateOktaFlow',
	async (context, { email }, thunkAPI) =>
	{
		try
		{
			thunkAPI.dispatch(uiSlice.actions.resetPostLoginProgress({ loginType: 'okta' }));

			const response = await api7.postUnauthorizedAsync<string>('/okta/initiate', {
				RedirectUri: `${window.location.origin}/okta/callback`,
				Email: email
			});

			if (response.ErrorCode === null && response.Success)
			{
				// Redirect to Okta
				window.location.href = response.Data;
				return response.Data;
			}
			else if (response.ErrorCode === 'invalid_credentials')
			{
				return thunkAPI.rejectWithValue({ code: 'invalid_credentials', message: response.ErrorMessage });
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e) 
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

export const initiateOktaHubRedirectFlow = createTracedAsyncThunk<string, void, { dispatch: AppDispatch, state: RootState }>(
	'auth/initiateOktaHubRedirectFlow',
	async (context, _args, thunkAPI) =>
	{
		try
		{
			thunkAPI.dispatch(uiSlice.actions.resetPostLoginProgress({ loginType: 'okta' }));

			const response = await api7.postUnauthorizedAsync<string>('/okta/hub-redirect', {
				RedirectUri: `${window.location.origin}/okta/callback`
			});

			if (response.ErrorCode === null && response.Success)
			{
				// Redirect to Okta
				window.location.href = response.Data;
				return response.Data;
			}
			else if (response.ErrorCode === 'invalid_credentials')
			{
				return thunkAPI.rejectWithValue({ code: 'invalid_credentials', message: response.ErrorMessage });
			}
			else 
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e) 
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

/**
 * Exchanges the OAuth authorization code for user authentication tokens.
 * Called by OktaHandler after Okta redirects back with code and state.
 * 
 * @param code - Authorization code from Okta
 * @param state - State parameter for CSRF protection
 */
export const oktaAuthentication = createTracedAsyncThunk<IUserAuthResponse, {code: string, state: string}, { dispatch: AppDispatch, state: RootState }>(
	'auth/oktaAuthentication',
	async (context, {code, state}, thunkAPI) => 
	{
		try 
		{
			thunkAPI.dispatch(uiSlice.actions.resetPostLoginProgress({ loginType: 'okta' }));

			const response = await api7.postUnauthorizedAsync<IUserAuthResponse>('/okta/exchange', { 
				RedirectUri: `${window.location.origin}/okta/callback`,
				State: state,
				Code: code
			});

			if (response.ErrorCode === null && response.Success) 
			{
				thunkAPI.dispatch(authSlice.actions.setAuthenticated(true));
				thunkAPI.dispatch(userInfoSlice.actions.setUserInfo(response.Data));
				
				globalSession.initializeSession(
					response.Data.AccessToken,
					response.Data.UserId,
					response.Data.FoundationId,
					response.Data.UserEmail,
					response.Data.UserName);

				globalSession.event(EventNavigationNames.LogIn, { [EventAuthTags.LoginMethod]: 'okta' });

				localStorage.setItem(LAST_LOGIN_TYPE_KEY, 'okta');

				return response.Data;
			}
			else 
			{
				thunkAPI.dispatch(authSlice.actions.setAuthenticated(false));
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e) 
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);

/**
 * Logs the user out of Okta.
 *
 * The backend revokes the tokens and clears the app session cookie, then returns the Okta OIDC
 * logout URL. The Okta SSO session lives in a browser cookie and can only be terminated by
 * navigating the browser to that URL — without it the SSO session survives and the next user on
 * the same machine would be silently signed in as the previous one. The caller must redirect the
 * browser to the returned URL.
 *
 * @returns The Okta logout URL to redirect the browser to, or undefined if none is available.
 */
export const oktaLogout = createTracedAsyncThunk<string | undefined, void, { dispatch: AppDispatch, state: RootState }>(
	'auth/oktaLogout',
	async (context, request, thunkAPI) =>
	{
		try
		{
			const response = await api7.postAsync<string | null>('/okta/logout', {
				PostLogoutRedirectUri: `${window.location.origin}/login`
			});

			if (response.ErrorCode === null && response.Success)
			{
				return thunkAPI.fulfillWithValue(response.Data ?? undefined);
			}
			else
			{
				return thunkAPI.rejectWithValue(response.ErrorMessage);
			}
		}
		// Likely a NetError thrown from the Api class
		catch (e)
		{
			return thunkAPI.rejectWithValue(e.message);
		}
	}
);


export interface IAuthState
{
	errorMessage: string;
	isAuthenticated: boolean;
	isError: boolean;
	isLoading: boolean;
	isLoginError: boolean;
	isSuccess: boolean;
	loginErrorMessage: string;
	manageableUsers: IManageUserResponse[];
	manageableUsersError: string | undefined;
	selectedUser: IManageUserResponse;
	successMessage: string;
	userAuthToken: string;
	troubleshooterUserList: IManageUserResponse[];
}

export const initialState: IAuthState = {
	errorMessage: undefined,
	isAuthenticated: false,
	isError: false,
	isLoading: false,
	isLoginError: false,
	isSuccess: undefined,
	loginErrorMessage: undefined,
	manageableUsers: undefined,
	manageableUsersError: undefined,
	selectedUser: undefined,
	successMessage: undefined,
	userAuthToken: undefined,
	troubleshooterUserList: undefined,
};

export const authSlice = createSlice({
	name: 'auth',
	initialState: initialState,
	reducers: {
		clearState: (state, { payload } : PayloadAction<string | undefined>) => 
		{
			state.isAuthenticated = false;
			state.isLoading = false;
			state.userAuthToken = undefined;

			// The caller can provide an error message if this was cleared due to an issue
			if (payload) 
			{
				state.errorMessage = payload;
				state.isError = true;
			}

			return state;
		},
		clearStateForLogout: (state, { payload } : PayloadAction<string | undefined>) =>
		{
			state.isAuthenticated = false;
			state.isLoading = false;
			state.userAuthToken = undefined;

			if (payload)
			{
				state.errorMessage = payload;
				state.isError = true;
			}
			
			localStorage.removeItem(LAST_LOGIN_TYPE_KEY);
			return state;
		},
		clearError: (state) =>
		{
			state.isError = false;
			state.isLoginError = false;
			state.errorMessage = undefined;
			state.loginErrorMessage = undefined;
		},
		clearManageableUsersError: (state) => 
		{
			state.manageableUsersError = undefined;
		},
		setAuthenticated: (state, { payload }: PayloadAction<boolean>) =>
		{
			state.isAuthenticated = payload;
		},
		setAuthToken: (state, { payload }: PayloadAction<IUserAuthResponse>) =>
		{
			state.userAuthToken = payload.AccessToken;
			if(payload && payload.AccessToken)
			{
				state.isAuthenticated = true;
			}
		},
		setManagedUser: (state, { payload }: PayloadAction<IManageUserResponse | undefined>) =>
		{
			state.selectedUser = payload;
		},
		clearSuccess: (state) =>
		{
			state.isSuccess = false;
			state.successMessage = undefined;

			return state;
		}
	},
	extraReducers: (builder) =>
	{
		builder.addCase(loginUser.pending, (state) =>
		{
			state.isLoading = true;
			state.isLoginError = false;
			state.loginErrorMessage = undefined;
			state.isError = false;
			state.isAuthenticated = false;
			state.errorMessage = undefined;
			state.userAuthToken = undefined;
			state.selectedUser = undefined;

		});
		builder.addCase(loginUser.fulfilled, (state) =>
		{
			state.isLoading = false;
			state.isAuthenticated = true;
			state.isLoginError = false;
			state.loginErrorMessage = undefined;
			state.isError = false;
			state.errorMessage = undefined;
		});
		builder.addCase(loginUser.rejected, (state, action) =>
		{
			handleAuthenticationRejection(state, action);
		});
		

		builder.addCase(tokenExchange.pending, (state) =>
		{
			state.isLoading = true;
			state.isLoginError = false;
			state.loginErrorMessage = undefined;
			state.isError = false;
			state.isAuthenticated = false;
			state.errorMessage = undefined;
			state.userAuthToken = undefined;
			state.selectedUser = undefined;
		});
		builder.addCase(tokenExchange.fulfilled, (state) =>
		{
			state.isLoading = false;
			state.isAuthenticated = true;
			state.isLoginError = false;
			state.loginErrorMessage = undefined;
			state.isError = false;
			state.errorMessage = undefined;
		});
		builder.addCase(tokenExchange.rejected, (state, action) =>
		{
			handleAuthenticationRejection(state, action);
		});

		builder.addCase(ssoExchange.pending, (state) =>
		{
			state.isLoading = true;
			state.selectedUser = undefined;
			state.isAuthenticated = false;
			state.userAuthToken = undefined;
			state.isLoginError = false;
			state.loginErrorMessage = undefined;
			state.isError = false;
			state.errorMessage = undefined;
		});

		builder.addCase(ssoExchange.fulfilled, (state, action) =>
		{
			state.isLoading = false;
			state.isAuthenticated = true;
			state.isLoginError = false;
			state.loginErrorMessage = undefined;
			state.isError = false;
			state.errorMessage = undefined;
			state.userAuthToken = action.payload.User.AccessToken;		
		});

		builder.addCase(ssoExchange.rejected, (state, action) => 
		{
			handleAuthenticationRejection(state, action);
		});

		builder.addCase(validateToken.pending, (state) =>
		{
			state.isLoading = true;
			state.isError = false;
			state.errorMessage = undefined;
		});
		builder.addCase(validateToken.fulfilled, (state, { payload }: PayloadAction<boolean>) =>
		{
			state.isLoading = false;
			state.isError = false;
			state.errorMessage = undefined;
			state.isAuthenticated = payload;
		});
		builder.addCase(validateToken.rejected, (state, action) =>
		{
			state.userAuthToken = undefined;
			state.isAuthenticated = false;
			state.isLoading = false;
			state.isError = true;
			if (action.payload)
			{
				state.errorMessage = 'There was an error authenticating.';
			}
			else
			{
				state.errorMessage = action.error.message;
			}
		});

		// manage users listing
		builder.addCase(getManageableUsers.fulfilled, (state, { payload }: PayloadAction<IManageUserResponse[]>) => 
		{
			state.manageableUsers = payload;
			// Update the selected user's data if we have one
			if (state.selectedUser !== undefined)
			{
				state.selectedUser = payload.find(mu => mu.UserId === state.selectedUser.UserId);
			}
			state.manageableUsersError = undefined;
		});
		builder.addCase(getManageableUsers.rejected, (state, action) =>
		{
			if (action.payload)
			{
				const payloadMessage = action.payload as string;
				if (payloadMessage.indexOf('network') > -1)
				{
					state.manageableUsersError = 'There was a problem contacting the server to get the list of manageable users. Please refresh to try again.';
				}
				else
				{
					state.manageableUsersError = 'There was a problem downloading the list of manageable users. Please refresh to try again.';
				}
			}
			else
			{
				state.manageableUsersError = action.error.message;
			}
		});
		// troubleshooting tools users listing
		builder.addCase(getTroubleshooterUsers.fulfilled, (state, { payload }: PayloadAction<IManageUserResponse[]>) => 
		{
			state.troubleshooterUserList = payload;
		});

		/**
		 * Okta Authentication Flow
		 */
		builder.addCase(initiateOktaFlow.pending, (state) =>
		{
			state.isLoading = true;
			state.isLoginError = false;
			state.loginErrorMessage = undefined;
			state.isError = false;
			state.errorMessage = undefined;
		});
		builder.addCase(initiateOktaFlow.fulfilled, (state) =>
		{
			state.isLoading = false;
		});
		builder.addCase(initiateOktaFlow.rejected, (state, action) =>
		{
			handleAuthenticationRejection(state, action);
		});

		builder.addCase(initiateOktaHubRedirectFlow.pending, (state) =>
		{
			state.isLoading = true;
			state.isLoginError = false;
			state.loginErrorMessage = undefined;
			state.isError = false;
			state.errorMessage = undefined;
		});
		builder.addCase(initiateOktaHubRedirectFlow.fulfilled, (state) =>
		{
			state.isLoading = false;
		});
		builder.addCase(initiateOktaHubRedirectFlow.rejected, (state, action) =>
		{
			handleAuthenticationRejection(state, action);
		});

		builder.addCase(oktaAuthentication.pending, (state) =>
		{
			state.isLoading = true;
			state.isLoginError = false;
			state.loginErrorMessage = undefined;
			state.isError = false;
			state.isAuthenticated = false;
			state.errorMessage = undefined;
			state.userAuthToken = undefined;
			state.selectedUser = undefined;
		});
		builder.addCase(oktaAuthentication.fulfilled, (state) =>
		{
			state.isLoading = false;
			state.isAuthenticated = true;
			state.isLoginError = false;
			state.loginErrorMessage = undefined;
			state.isError = false;
			state.errorMessage = undefined;
		});
		builder.addCase(oktaAuthentication.rejected, (state, action) =>
		{
			handleAuthenticationRejection(state, action);
		});

		/**
		 * User Password Recovery Request
		 */
		builder.addCase(requestPasswordReset.pending, (state) =>
		{
			state.isLoading = true;
		});
		builder.addCase(requestPasswordReset.fulfilled, (state) =>
		{
			state.isLoading = false;

			// If a success message is passed in, display it
			state.successMessage = 'An email has been sent containing instructions on how to reset your password.';

			state.isSuccess = true;
		});
		builder.addCase(requestPasswordReset.rejected, (state, action) =>
		{
			state.isLoading = false;
			state.isError = true;
			if (action.payload)
			{
				const payload = action.payload as {error: string, growerId: string};
				const errorMessage = payload.error;
				if (errorMessage)
				{
					state.errorMessage = errorMessage;
				}
				else
				{
					state.errorMessage = 'There was a problem contacting the server to request a password recovery email. Please try again.';
				}
			}
			else
			{
				state.errorMessage = 'There was a problem requesting a password recovery email. Please try again.';
			}
		});
	}
});

/**
 * A selector to get what user context we need to use
 */
export const getCurrentActingUser = (state: RootState, getManagingUser: boolean = false) =>
{
	// Return the logged in user (managing user) regardless of a selected user
	if (getManagingUser)
	{
		return state.userInfo;
	} 
	
	// If there is a selected user, return the selected user's data
	if (state.auth.selectedUser)
	{
		return state.auth.selectedUser;
	}

	// Otherwise return the authenticated user's data
	return state.userInfo;
};

export const canManageAccounts = (state: RootState) =>
{
	return state.auth.manageableUsers?.length > 1;
};

export const isManagingAccount = (state: RootState) =>
{
	return state.userInfo.UserId !== getCurrentActingUser(state).UserId;
};

export const hasEffectivePermission = (state: RootState, permission: UserPermission, getManagingUser: boolean = false) =>
{
	return getCurrentActingUser(state, getManagingUser).Permissions?.indexOf(permission) >= 0;
};

export const { clearState, clearStateForLogout, clearError, setManagedUser, clearSuccess } = authSlice.actions;

export const authSelector = (state: RootState) => state.auth;

/**
 * A function for handling the common cases where authentication didn't work. 
 */
function handleAuthenticationRejection(state: IAuthState, action) 
{
	state.userAuthToken = undefined;
	state.isAuthenticated = false;
	state.isLoading = false;
	state.isLoginError = true;

	if(initiateOktaFlow.rejected.match(action) || initiateOktaHubRedirectFlow.rejected.match(action))
	{
		if(typeof action.payload === 'object' && (action.payload as any)?.code === 'invalid_credentials')
		{
			state.loginErrorMessage = (action.payload as any).message;
		}
		else
		{
			state.loginErrorMessage = 'There was a problem initiating the authentication flow. Please try again later.';
		}
	}
	else if (typeof action.payload === 'string') 
	{
		const payloadMessage = action.payload as string;
		if (payloadMessage.indexOf('network') > -1) 
		{
			state.loginErrorMessage = 'There was a problem contacting the server. Please try again.';
		}
		else if (payloadMessage.indexOf('credentials') > -1) 
		{
			state.loginErrorMessage = 'The provided credentials are incorrect. Please try again.';
		}
		else if (payloadMessage.indexOf(action.meta.arg.username) > -1) 
		{
			state.loginErrorMessage = payloadMessage;
		}
		else if (payloadMessage.indexOf('locked') > -1)
		{
			state.loginErrorMessage = payloadMessage;
		}
		else 
		{
			state.loginErrorMessage = 'There was a problem contacting the server. Please try again.';
		}
	}
	else if(typeof action.payload === 'object' && (action.payload as any).code)
	{
		state.loginErrorMessage = (action.payload as any).message;
	}
	else 
	{
		state.loginErrorMessage = 'There was a problem contacting the server. Please try again.';
	}
}
